import { Fragment, FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { classNames } from "@/utils/classNames";

import { ROUTE_CONFIG } from "@/routes/config";

import { DashboardListProspectPreview, DashboardListWithNewProspects } from "../schema/dashboard";
import { PROCESSING_TONE_CLASSES, processingStatusPill } from "../utils/processingStatusPill";

export const prospectPreviewName = (prospect: DashboardListProspectPreview): string => {
	const name = [prospect.firstName?.trim(), prospect.lastName?.trim()].filter(Boolean).join(" ");

	return name || prospect.title?.trim() || "";
};

// Lists are filtered on the Ready-to-send screen via a JSON `listIds` param of
// `{ id, title }` items (see ReadyToSend.getSelectedListsFromUrl).
const reviewListUrl = (list: DashboardListWithNewProspects): string => {
	const listIds = JSON.stringify([{ id: list.id, title: list.name }]);

	return `${ROUTE_CONFIG.MESSAGE_CENTER}?listIds=${encodeURIComponent(listIds)}`;
};

interface ListProspectsRowProps {
	list: DashboardListWithNewProspects;
}

export const ListProspectsRow: FunctionComponent<ListProspectsRowProps> = ({ list }) => {
	const { t } = useTranslation();

	const pill = processingStatusPill(list.processingStatus);
	const detailUrl = `${ROUTE_CONFIG.LISTS_DETAIL}/${list.id}`;

	const previewItems = list.prospects
		.map((prospect) => ({ key: prospect.listPersonId, name: prospectPreviewName(prospect) }))
		.filter((item) => item.name);
	const remaining = list.newProspectCount - previewItems.length;

	return (
		<div className="group relative px-4 py-3 hover:bg-background-light">
			<div className="flex items-center gap-2 min-w-0">
				<Link className="text-sm font-semibold text-text truncate hover:underline" to={detailUrl}>
					{list.name}
				</Link>
				<span className="shrink-0 inline-flex items-center h-5 px-2 rounded-full text-[11px] font-semibold bg-action-200 text-brand-light">
					+{list.newProspectCount}
				</span>
				{pill.tone !== "neutral" && (
					<span
						className={classNames(
							"shrink-0 inline-flex items-center h-5 px-2 rounded-full text-[10px] font-semibold uppercase tracking-wide",
							PROCESSING_TONE_CLASSES[pill.tone]
						)}
					>
						{t(pill.labelKey)}
					</span>
				)}
				{pill.tone !== "neutral" &&
					(list.processingProgressPercent != null || list.processingStatusMessage) && (
						<span className="shrink-0 text-[11px] text-text-meta">
							{list.processingProgressPercent != null
								? `${list.processingProgressPercent}%`
								: list.processingStatusMessage}
						</span>
					)}
			</div>

			{previewItems.length > 0 && (
				<div className="relative mt-1">
					<div className="text-xs text-text-meta truncate transition-opacity group-hover:opacity-0">
						{t("dashboard.newLeads.including")}{" "}
						{previewItems.map((item, index) => (
							<Fragment key={item.key}>
								{index > 0 && ", "}
								<span className="text-text font-medium">{item.name}</span>
							</Fragment>
						))}
						{remaining > 0 && ` ${t("dashboard.newLeads.andMore", { count: remaining })}`}
					</div>

					<div className="absolute inset-0 flex items-center gap-2 opacity-0 pointer-events-none transition-opacity group-hover:opacity-100 group-hover:pointer-events-auto">
						<Link
							className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-brand-light text-white hover:opacity-90"
							to={reviewListUrl(list)}
						>
							{t("dashboard.newLeads.reviewAll")}
						</Link>
						<Link
							className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border border-border-light text-text hover:bg-white"
							to={detailUrl}
						>
							{t("dashboard.newLeads.openList")}
						</Link>
					</div>
				</div>
			)}
		</div>
	);
};
